// src/components/EventDetail.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { db } from '../firebaseConfig';
import {
  doc,
  getDoc,
  collection,
  addDoc,
  deleteDoc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  where,
  updateDoc,
  writeBatch
} from 'firebase/firestore';
import { useTranslation } from 'react-i18next'; // <-- MODIFICA: Importa hook

// --- IMPORT MUI (Invariati) ---
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Paper,
  Divider,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  ListItemButton,
  Checkbox,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ScheduleIcon from '@mui/icons-material/Schedule';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import EditIcon from '@mui/icons-material/Edit';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import ListAltIcon from '@mui/icons-material/ListAlt';

// --- Helper Formato Data (MODIFICATI con t()) ---
const formatTimestampForDisplay = (timestamp, lang) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const locale = lang === 'it' ? 'it-IT' : 'en-GB';
    return date.toLocaleString(locale, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};
const formatTimestampToInputDate = (timestamp) => {
  if (!timestamp) return '';
  const date = timestamp.toDate();
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().split('T')[0];
};
const formatTimestampToInputDateTime = (timestamp) => {
  if (!timestamp) return '';
  const date = timestamp.toDate();
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 16);
};
// --- FINE HELPER ---


// --- MODAL ASSEGNAZIONE VETTURE (MODIFICATO con t()) ---
const AssignVehiclesModal = ({ open, onClose, allVehicles, assignedVehicleIds, onSave, t }) => {
  const [selectedIds, setSelectedIds] = useState(assignedVehicleIds);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setSelectedIds(assignedVehicleIds);
  }, [assignedVehicleIds, open]);

  const handleToggle = (vehicleId) => {
    setSelectedIds((prev) =>
      prev.includes(vehicleId) ? prev.filter(id => id !== vehicleId) : [...prev, vehicleId]
    );
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(selectedIds); 
    } catch (error) {
      console.error("Error during save operation:", error);
      alert(t('modal_assign_vehicles_error', { message: error.message })); 
    } finally {
      setIsSaving(false);
      onClose(); 
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t('modal_assign_vehicles_title')}</DialogTitle>
      <DialogContent>
        <List dense>
          {allVehicles.length === 0 && <Typography>{t('modal_assign_vehicles_empty')}</Typography>}
          {allVehicles.map((vehicle) => {
            const isChecked = selectedIds.includes(vehicle.id);
            return (
              <ListItem key={vehicle.id} disablePadding>
                <ListItemButton onClick={() => handleToggle(vehicle.id)}>
                  <ListItemIcon><Checkbox edge="start" checked={isChecked} tabIndex={-1} /></ListItemIcon>
                  <ListItemText primary={`${vehicle.marca} ${vehicle.modello}`} secondary={`Nr. ${vehicle.numeroGara || 'N/A'}`} />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>{t('modal_general_cancel')}</Button>
        <Button onClick={handleSave} variant="contained" disabled={isSaving}>
          {isSaving ? <CircularProgress size={24} /> : t('modal_general_save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// --- MODAL Gestione Checklist (MODIFICATO con t()) ---
const ManageVehicleChecklistsModal = ({ open, onClose, allTemplates, vehicle, eventChecklists, onSave, teamId, t }) => {
  
  const [selectedTemplateIds, setSelectedTemplateIds] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [initialTemplateIds, setInitialTemplateIds] = useState([]);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [isAddingTemplate, setIsAddingTemplate] = useState(false);

  useEffect(() => {
    if (open && vehicle && eventChecklists) {
      const vehicleChecklists = eventChecklists.filter(cl => cl.vehicleId === vehicle.id);
      const ids = vehicleChecklists.map(cl => cl.templateId);
      setSelectedTemplateIds(ids);
      setInitialTemplateIds(ids); 
    }
  }, [open, vehicle, eventChecklists]);

  const handleToggle = (templateId) => {
    setSelectedTemplateIds((prev) =>
      prev.includes(templateId) ? prev.filter(id => id !== templateId) : [...prev, templateId]
    );
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(vehicle, initialTemplateIds, selectedTemplateIds);
    } catch (error) {
      console.error("Error during save operation:", error);
      alert(t('modal_manage_checklists_error_save', { message: error.message }));
    } finally {
      setIsSaving(false);
      onClose();
    }
  };

  const handleAddNewTemplate = async (e) => {
    e.preventDefault();
    const name = newTemplateName.trim();
    if (!name) return;
    
    if (allTemplates.some(t => t.name.toLowerCase() === name.toLowerCase())) {
      alert(t('modal_manage_checklists_error_exists'));
      return;
    }

    setIsAddingTemplate(true);
    try {
      const docRef = await addDoc(collection(db, "checklistTemplates"), {
        teamId: teamId,
        name: name,
        items: [], 
        createdAt: serverTimestamp()
      });
      setSelectedTemplateIds(prev => [...prev, docRef.id]);
      setNewTemplateName(''); 
    } catch (error) {
      console.error("Error adding new template:", error);
      alert(t('modal_manage_checklists_error_add', { message: error.message }));
    } finally {
      setIsAddingTemplate(false);
    }
  };
  
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t('modal_manage_checklists_title')}</DialogTitle>
      <Typography variant="h6" sx={{ pl: 3, pr: 3, mb: 1 }}>
        {vehicle?.marca} {vehicle?.modello}
      </Typography>
      <DialogContent>
        <Typography variant="subtitle1" gutterBottom>{t('modal_manage_checklists_select_existing')}</Typography>
        <List dense sx={{ maxHeight: 200, overflowY: 'auto' }}>
          {allTemplates.length === 0 && <Typography>{t('modal_manage_checklists_empty')}</Typography>}
          {allTemplates.map((template) => {
            const isChecked = selectedTemplateIds.includes(template.id);
            return (
              <ListItem key={template.id} disablePadding>
                <ListItemButton onClick={() => handleToggle(template.id)}>
                  <ListItemIcon><Checkbox edge="start" checked={isChecked} tabIndex={-1} /></ListItemIcon>
                  <ListItemText primary={template.name} />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
        
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle1" gutterBottom>
          {t('modal_manage_checklists_create_new')}
        </Typography>
        <Box component="form" onSubmit={handleAddNewTemplate} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            label={t('modal_manage_checklists_new_name')}
            value={newTemplateName}
            onChange={(e) => setNewTemplateName(e.target.value)}
            size="small"
            fullWidth
            disabled={isAddingTemplate}
          />
          <Button
            type="submit"
            variant="outlined"
            size="medium"
            disabled={isAddingTemplate || !newTemplateName}
            startIcon={isAddingTemplate ? <CircularProgress size={16} /> : <AddIcon />}
            sx={{ height: '40px' }}
          >
            {t('modal_manage_checklists_create_button')}
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>{t('modal_general_cancel')}</Button>
        <Button onClick={handleSave} variant="contained" disabled={isSaving}>
          {isSaving ? <CircularProgress size={24} /> : t('modal_general_save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
// --- FINE MODAL ---


function EventDetail({ teamId, userRole }) {
  // --- MODIFICA: Inizializza t() ---
  const { t, i18n } = useTranslation('event');
  const currentLang = i18n.language;
  // --- FINE MODIFICA ---

  // Stati Evento (Invariati)
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [isLoadingEvent, setIsLoadingEvent] = useState(true);
  const [isLoadingSessions, setIsLoadingSessions] = useState(true);
  const [eventSetups, setEventSetups] = useState({});
  const [eventChecklists, setEventChecklists] = useState({});
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [trackDetails, setTrackDetails] = useState(null);
  const [isLoadingTrack, setIsLoadingTrack] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false); 
  const isManager = userRole === 'admin' || userRole === 'Engineer';
  const [sessionName, setSessionName] = useState('');
  const [startTime, setStartTime] = useState('');
  const [sessionDuration, setSessionDuration] = useState('');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [tracksList, setTracksList] = useState([]); 
  const [isLoadingTracksList, setIsLoadingTracksList] = useState(true); 
  const [editFormData, setEditFormData] = useState({ eventName: '', startDate: '', endDate: '', selectedTrack: null, liveTimingUrl: '' });
  const [isSessionModalOpen, setIsSessionModalOpen] = useState(false);
  const [currentSession, setCurrentSession] = useState(null); 
  const [editSessionFormData, setEditSessionFormData] = useState({ sessionName: '', startTime: '', duration: '' });
  const [allVehicles, setAllVehicles] = useState([]); 
  const [isLoadingVehicles, setIsLoadingVehicles] = useState(true);
  const [allChecklistTemplates, setAllChecklistTemplates] = useState([]); 
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(true);
  const [allEventChecklists, setAllEventChecklists] = useState([]); 
  const [isLoadingAllEventChecklists, setIsLoadingAllEventChecklists] = useState(true);
  const [isVehicleModalOpen, setIsVehicleModalOpen] = useState(false);
  const [isVehicleChecklistModalOpen, setIsVehicleChecklistModalOpen] = useState(false);
  const [currentVehicleForModal, setCurrentVehicleForModal] = useState(null);

  // --- useEffects (Invariati, eccetto 't' nelle dipendenze) ---
  useEffect(() => {
    if (!eventId || !teamId) return;
    setIsLoadingEvent(true); setIsLoadingTrack(true); setTrackDetails(null);
    const eventRef = doc(db, "events", eventId);
    const unsubEvent = onSnapshot(eventRef, (docSnap) => {
      if (docSnap.exists() && docSnap.data().teamId === teamId) {
        const eventData = docSnap.data(); setEvent(eventData);
        if (eventData.trackId) {
          const trackRef = doc(db, "tracks", eventData.trackId);
          getDoc(trackRef).then(trackSnap => {
            if (trackSnap.exists()) { setTrackDetails(trackSnap.data()); } 
            else { setTrackDetails(null); }
            setIsLoadingTrack(false);
          });
        } else { setTrackDetails(null); setIsLoadingTrack(false); }
      } else { console.error("Event not found"); setEvent(null); setIsLoadingTrack(false); }
      setIsLoadingEvent(false);
    }, (err) => { console.error(t('detail_loading_error'), err); setIsLoadingEvent(false); setIsLoadingTrack(false); });
    return () => unsubEvent();
  }, [eventId, teamId, t]); // Aggiunto t

  useEffect(() => {
    if (!eventId || !teamId) return;
    setIsLoadingData(true);
    const sessionsQuery = query(collection(db, "events", eventId, "sessions"), orderBy("startTime", "asc"));
    const unsubSessions = onSnapshot(sessionsQuery, (sessionsSnap) => {
      const sessionsList = sessionsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setSessions(sessionsList); setIsLoadingSessions(false);
    }, (error) => { console.error("Error session listener:", error.message); setIsLoadingSessions(false); });
    const setupsQuery = query(collection(db, "setupSheets"), where("eventId", "==", eventId), where("teamId", "==", teamId));
    const unsubSetups = onSnapshot(setupsQuery, (setupsSnap) => {
      const setupsMap = {}; const setupIds = [];
      setupsSnap.forEach(doc => {
        const setup = doc.data();
        if (setup.sessionId) { setupsMap[setup.sessionId] = { id: doc.id, ...setup }; setupIds.push(doc.id); }
      });
      setEventSetups(setupsMap);
      if (setupIds.length === 0) { setEventChecklists({}); setIsLoadingData(false); return; }
      const maxIn = 30; const chunks = [];
      for(let i = 0; i < setupIds.length; i += maxIn) { chunks.push(setupIds.slice(i, i + maxIn)); }
      const checklistUnsubs = []; let loadedChunks = 0; const allChecklistsMap = {};
      chunks.forEach(chunk => {
        const checklistsQuery = query(collection(db, "setupChecklists"), where("setupId", "in", chunk));
        const unsubChecklistChunk = onSnapshot(checklistsQuery, (checklistsSnap) => {
            checklistsSnap.forEach(doc => { allChecklistsMap[doc.data().setupId] = doc.data(); });
            loadedChunks++;
            if (loadedChunks === chunks.length) { setEventChecklists(allChecklistsMap); setIsLoadingData(false); }
        }, (error) => { console.error("Error checklist listener:", error.message); setIsLoadingData(false); });
        checklistUnsubs.push(unsubChecklistChunk);
      });
      return () => checklistUnsubs.forEach(unsub => unsub());
    }, (error) => { console.error("Error setup listener:", error.message); setIsLoadingData(false); });
    return () => { unsubSessions(); unsubSetups(); };
  }, [eventId, teamId]);

  useEffect(() => {
    if (!teamId) return;
    setIsLoadingTracksList(true);
    const tracksQuery = query(collection(db, "tracks"), where("teamId", "==", teamId), orderBy("nome", "asc"));
    const unsubscribe = onSnapshot(tracksQuery, (qs) => {
      setTracksList(qs.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setIsLoadingTracksList(false);
    }, (err) => { console.error("Error loading tracks list:", err); setIsLoadingTracksList(false); });
    return () => unsubscribe();
  }, [teamId]);

  useEffect(() => {
    if (!teamId || !isManager) {
      setIsLoadingVehicles(false);
      return;
    }
    setIsLoadingVehicles(true);
    const q = query(collection(db, "vehicles"), where("teamId", "==", teamId), orderBy("marca"));
    const unsub = onSnapshot(q, (qs) => {
      setAllVehicles(qs.docs.map(d => ({ id: d.id, ...d.data() })));
      setIsLoadingVehicles(false);
    }, (err) => { console.error(err); setIsLoadingVehicles(false); });
    return () => unsub();
  }, [teamId, isManager]);

  useEffect(() => {
    if (!teamId || !isManager) {
      setIsLoadingTemplates(false);
      return;
    }
    setIsLoadingTemplates(true);
    const q = query(collection(db, "checklistTemplates"), where("teamId", "==", teamId), orderBy("name"));
    const unsub = onSnapshot(q, (qs) => {
      setAllChecklistTemplates(qs.docs.map(d => ({ id: d.id, ...d.data() })));
      setIsLoadingTemplates(false);
    }, (err) => { console.error(err); setIsLoadingTemplates(false); });
    return () => unsub();
  }, [teamId, isManager]);

  useEffect(() => {
    if (!teamId || !eventId) return;
    setIsLoadingAllEventChecklists(true);
    const q = query(collection(db, "eventChecklists"), where("eventId", "==", eventId));
    const unsub = onSnapshot(q, (qs) => {
      setAllEventChecklists(qs.docs.map(d => ({ id: d.id, ...d.data() })));
      setIsLoadingAllEventChecklists(false);
    }, (err) => { console.error(err); setIsLoadingAllEventChecklists(false); });
    return () => unsub();
  }, [teamId, eventId]);
  
  
  // --- Funzioni Handler (MODIFICATE con t()) ---
  const handleAddSession = async (e) => {
    e.preventDefault();
    if (!sessionName || !startTime || !sessionDuration) { alert(t('session_add_error_fields')); return; }
    const durationInMinutes = Number(sessionDuration);
    if (isNaN(durationInMinutes) || durationInMinutes <= 0) { alert(t('session_add_error_duration')); return; }
    const firestoreTimestamp = Timestamp.fromDate(new Date(startTime));
    setIsSubmitting(true);
    try {
      await addDoc(collection(db, "events", eventId, "sessions"), {
        sessionName: sessionName.trim(), startTime: firestoreTimestamp, duration: durationInMinutes, createdAt: serverTimestamp()
      });
      setSessionName(''); setStartTime(''); setSessionDuration('');
    } catch (error) { console.error("Error adding session:", error); alert(t('session_add_error_generic')); }
    setIsSubmitting(false);
  };
  const handleDeleteSession = async (sessionId) => {
    if (window.confirm(t('session_delete_confirm'))) {
        try { await deleteDoc(doc(db, "events", eventId, "sessions", sessionId)); }
        catch (error) { console.error("Error deleting:", error); alert(t('session_delete_error')); }
    }
  };
  const getSessionStatusIcon = (session) => {
    const now = new Date();
    const sessionStartTime = session.startTime?.toDate();
    if (!sessionStartTime || sessionStartTime > now) return null;
    const setup = eventSetups[session.id];
    if (!setup) return ( <Tooltip title={t('session_tooltip_no_setup')}><HelpOutlineIcon color="disabled" /></Tooltip> );
    const checklist = eventChecklists[setup.id];
    if (!checklist) return ( <Tooltip title={t('session_tooltip_no_checklist')}><WarningAmberIcon color="warning" /></Tooltip> );
    const isComplete = checklist.items.length > 0 && checklist.items.every(item => item.checked);
    if (isComplete) return ( <Tooltip title={t('session_tooltip_complete')}><CheckCircleOutlineIcon color="success" /></Tooltip> );
    else return ( <Tooltip title={t('session_tooltip_incomplete')}><WarningAmberIcon color="error" /></Tooltip> );
  };
  
  const handleOpenEditModal = () => {
    if (!event) return;
    const trackObj = tracksList.find(t => t.id === event.trackId) || null;
    setEditFormData({
      eventName: event.eventName,
      startDate: formatTimestampToInputDate(event.startDate),
      endDate: formatTimestampToInputDate(event.endDate),
      selectedTrack: trackObj,
      liveTimingUrl: event.liveTimingUrl || '',
    });
    setIsEditModalOpen(true);
  };
  const handleCloseEditModal = () => { setIsEditModalOpen(false); };
  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };
  const handleUpdateEvent = async (e) => {
    e.preventDefault();
    const { eventName, startDate, endDate, selectedTrack } = editFormData;
    if (!eventName || !startDate || !endDate) { alert(t('modal_edit_event_error_all_fields')); return; }
    const start = new Date(startDate);
    const end = new Date(endDate);
    start.setMinutes(start.getMinutes() + start.getTimezoneOffset());
    end.setMinutes(end.getMinutes() + end.getTimezoneOffset());
    if (end < start) { alert(t('modal_edit_event_error_date')); return; }
    setIsSubmitting(true);
    const eventRef = doc(db, "events", eventId);
    try {
      await updateDoc(eventRef, {
        eventName: eventName.trim(),
        trackName: selectedTrack ? selectedTrack.nome : '',
        trackId: selectedTrack ? selectedTrack.id : null,
        startDate: start,
        endDate: end,
        liveTimingUrl: editFormData.liveTimingUrl?.trim() || '',
      });
      handleCloseEditModal();
    } catch (error) { console.error("Error updating event:", error); alert(t('modal_edit_event_error_update'));
    } finally { setIsSubmitting(false); }
  };
  
  const handleOpenEditSessionModal = (session) => {
    setCurrentSession(session);
    setEditSessionFormData({
      sessionName: session.sessionName || '',
      startTime: formatTimestampToInputDateTime(session.startTime),
      duration: session.duration || ''
    });
    setIsSessionModalOpen(true);
  };
  const handleCloseEditSessionModal = () => {
    setIsSessionModalOpen(false);
    setCurrentSession(null);
  };
  const handleEditSessionFormChange = (e) => {
    const { name, value } = e.target;
    setEditSessionFormData(prev => ({ ...prev, [name]: value }));
  };
  const handleUpdateSession = async (e) => {
    e.preventDefault();
    const { sessionName, startTime, duration } = editSessionFormData;
    if (!sessionName || !startTime || !duration) { alert(t('modal_edit_session_error_fields')); return; }
    const durationInMinutes = Number(duration);
    if (isNaN(durationInMinutes) || durationInMinutes <= 0) { alert(t('modal_edit_session_error_duration')); return; }
    const firestoreTimestamp = Timestamp.fromDate(new Date(startTime));
    setIsSubmitting(true);
    const sessionRef = doc(db, "events", eventId, "sessions", currentSession.id);
    try {
      await updateDoc(sessionRef, {
        sessionName: sessionName.trim(),
        startTime: firestoreTimestamp,
        duration: durationInMinutes
      });
      handleCloseEditSessionModal();
    } catch (error) { console.error("Error updating session:", error); alert(t('modal_edit_session_error_update'));
    } finally { setIsSubmitting(false); }
  };

  const assignedVehicleIds = useMemo(() => event?.assignedVehicleIds || [], [event]);
  const assignedVehicles = useMemo(() => {
    return allVehicles.filter(v => assignedVehicleIds.includes(v.id));
  }, [allVehicles, assignedVehicleIds]);
  
  const handleSaveVehicleAssignments = useCallback(async (newVehicleIds) => {
    const batch = writeBatch(db);
    const eventRef = doc(db, "events", eventId);
    const currentIds = assignedVehicleIds;
    const toRemove = currentIds.filter(id => !newVehicleIds.includes(id));
    batch.update(eventRef, { assignedVehicleIds: newVehicleIds });
    toRemove.forEach(vehicleId => {
      const checklistsToRemove = allEventChecklists.filter(cl => cl.vehicleId === vehicleId);
      checklistsToRemove.forEach(cl => {
        batch.delete(doc(db, "eventChecklists", cl.id));
      });
    });
    try {
      await batch.commit();
    } catch (error) {
      console.error("Error saving vehicle assignments:", error);
      throw new Error(error.message); 
    }
  }, [eventId, assignedVehicleIds, allEventChecklists]);

  const handleSaveVehicleChecklists = useCallback(async (vehicle, initialTemplateIds, newTemplateIds) => {
    const batch = writeBatch(db);
    const toAdd = newTemplateIds.filter(id => !initialTemplateIds.includes(id));
    const toRemove = initialTemplateIds.filter(id => !newTemplateIds.includes(id));
    toAdd.forEach(templateId => {
      const template = allChecklistTemplates.find(t => t.id === templateId);
      if (template && vehicle) {
        const newDocRef = doc(collection(db, "eventChecklists"));
        batch.set(newDocRef, {
          teamId, eventId, vehicleId: vehicle.id, templateId,
          vehicleName: `${vehicle.marca} ${vehicle.modello}`,
          templateName: template.name,
          items: template.items.map(txt => ({ text: txt, checked: false })),
          isComplete: false,
        });
      }
    });
    toRemove.forEach(templateId => {
      const checklistDoc = allEventChecklists.find(
        cl => cl.vehicleId === vehicle.id && cl.templateId === templateId
      );
      if (checklistDoc) {
        batch.delete(doc(db, "eventChecklists", checklistDoc.id));
      }
    });
    try {
      await batch.commit();
    } catch (error) {
      console.error("Error saving vehicle-specific checklists:", error);
      throw new Error(error.message);
    }
  }, [teamId, eventId, allChecklistTemplates, allEventChecklists]); 
  
  const handleOpenVehicleChecklistModal = (vehicle) => {
    setCurrentVehicleForModal(vehicle);
    setIsVehicleChecklistModalOpen(true);
  };
  const handleCloseVehicleChecklistModal = () => {
    setCurrentVehicleForModal(null);
    setIsVehicleChecklistModalOpen(false);
  };
  
  const paperStyle = {
    p: { xs: 2, sm: 3 },
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'background.paper'
  };

  const isPageLoading = isLoadingEvent || isLoadingData || isLoadingTracksList || isLoadingVehicles || isLoadingTemplates || isLoadingAllEventChecklists;
  if (isPageLoading) { return ( <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}><CircularProgress /></Box> ); }
  
  if (!event) { return ( <Box><Typography color="error">{t('detail_not_found')}</Typography></Box> ); }

  // --- RENDER (MODIFICATO con t()) ---
  return (
    <>
      {/* 1. Header */}
      <Paper elevation={0} sx={{ ...paperStyle, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <IconButton color="inherit" component={RouterLink} to="/events-calendar" sx={{ mr: 2, p: 0.5 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700, flexGrow: 1 }}>
            {event.eventName}
          </Typography>
          {isManager && (
            <Tooltip title={t('edit_event_button')}>
              <IconButton color="inherit" onClick={handleOpenEditModal} disabled={isLoadingTracksList}>
                <EditIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        
        {isLoadingTrack && <CircularProgress size={20} />}
        {!isLoadingTrack && trackDetails && (
        <Box sx={{ pl: 6 }}>
          <Typography variant="h6" color="text.secondary">
            {t('track_prefix')} {trackDetails.nome} {trackDetails.lengthMeters ? `(${trackDetails.lengthMeters} m)` : ''}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('address_prefix', { ns: 'track' })} {trackDetails.address || 'N/A'} {/* Riusa da 'track' */}
          </Typography>
        </Box>
        )}
        {!isLoadingTrack && !trackDetails && event.trackName && (
          <Typography variant="h6" color="text.secondary" sx={{ pl: 6 }}>{t('track_prefix')} {event.trackName} ({t('track_manual')})</Typography>
        )}
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1, pl: 6 }}>
          {t('date_from')} {event.startDate ? event.startDate.toDate().toLocaleDateString(currentLang === 'it' ? 'it-IT' : 'en-GB') : 'N/A'}
          {' '}{t('date_to')}{' '}
          {event.endDate ? event.endDate.toDate().toLocaleDateString(currentLang === 'it' ? 'it-IT' : 'en-GB') : 'N/A'}
        </Typography>
      </Paper>

      {/* Logistica Evento */}
      {isManager && (
        <Paper elevation={0} sx={{...paperStyle, mb: 3}}>
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h5">{t('logistics_title', { count: assignedVehicleIds.length })}</Typography>
              <Button 
                size="small" 
                startIcon={<EditIcon />} 
                onClick={() => setIsVehicleModalOpen(true)}
                disabled={isLoadingVehicles}
              >
                {t('logistics_manage_button')}
              </Button>
            </Box>
            <Divider sx={{ my: 1 }} />
            {isLoadingVehicles ? <CircularProgress size={24} /> : (
              <List dense>
                {assignedVehicles.length === 0 && <ListItemText primary={t('logistics_empty')} sx={{fontStyle: 'italic'}} />}
                {assignedVehicles.map(v => {
                  const vehicleChecklistCount = allEventChecklists.filter(cl => cl.vehicleId === v.id).length;
                  return (
                    <ListItem 
                      key={v.id} 
                      sx={{pl: 0}}
                      secondaryAction={
                        <Tooltip title={t('logistics_checklists_manage_tooltip')}>
                          <IconButton edge="end" onClick={() => handleOpenVehicleChecklistModal(v)}>
                            <ListAltIcon />
                          </IconButton>
                        </Tooltip>
                      }
                    >
                      <ListItemIcon sx={{minWidth: 40}}><DirectionsCarIcon /></ListItemIcon>
                      <ListItemText 
                        primary={`${v.marca} ${v.modello}`} 
                        secondary={`Nr. ${v.numeroGara || 'N/A'} | ${t('logistics_checklists_prefix')} ${vehicleChecklistCount}`} 
                      />
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Box>
        </Paper>
      )}

      {/* Paper Gestione Sessioni */}
      <Paper elevation={0} sx={{ ...paperStyle, mb: 3 }}>
        <Typography variant="h5" gutterBottom>{t('sessions_title')}</Typography>
        
        {isManager && (
          <Box component="form" onSubmit={handleAddSession} sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5, alignItems: 'flex-start' }}>
              <TextField label={t('session_name_label')} value={sessionName} onChange={(e) => setSessionName(e.target.value)} required fullWidth sx={{ flex: 3 }} />
              <TextField label={t('session_time_label')} type="datetime-local" value={startTime} onChange={(e) => setStartTime(e.target.value)} required fullWidth InputLabelProps={{ shrink: true }} sx={{ flex: 3 }} />
              <TextField label={t('session_duration_label')} type="number" value={sessionDuration} onChange={(e) => setSessionDuration(e.target.value)} required fullWidth sx={{ flex: 1 }} />
              <Button type="submit" variant="contained" disabled={isSubmitting} startIcon={isSubmitting ? <CircularProgress size={20} /> : <AddIcon />} size="large" sx={{ height: '56px', width: { xs: '100%', md: 'auto' } }}>
                {t('session_add_button')}
              </Button>
            </Box>
          </Box>
        )}
        
        {isManager && <Divider sx={{ my: 2 }} />}

        {(isLoadingSessions || isLoadingData) && <CircularProgress size={24} />}
        {!isLoadingSessions && sessions.length === 0 && ( <Typography variant="body2" sx={{ fontStyle: 'italic' }}>{t('sessions_empty')}</Typography> )}
        
        {!isLoadingSessions && sessions.length > 0 && (
          <List>
            {sessions.map((session) => (
              <ListItem
                key={session.id}
                divider
                sx={{ backgroundColor: 'background.default', border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 1 }}
                secondaryAction={ isManager && ( 
                  <Box>
                    <IconButton edge="end" aria-label="edit session" onClick={() => handleOpenEditSessionModal(session)} sx={{ mr: 0.5 }}>
                      <EditIcon />
                    </IconButton>
                    <IconButton edge="end" aria-label="delete session" onClick={() => handleDeleteSession(session.id)}> 
                      <DeleteIcon color="error" /> 
                    </IconButton>
                  </Box>
                )}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {getSessionStatusIcon(session) || <ScheduleIcon />}
                </ListItemIcon>
                <ListItemText
                  primary={session.sessionName}
                  primaryTypographyProps={{ fontWeight: 600 }}
                  secondary={`${t('session_start_prefix')} ${formatTimestampForDisplay(session.startTime, currentLang)} ${session.duration ? `(${session.duration} ${t('session_duration_suffix')})` : ''}`}
                />
              </ListItem>
            ))}
          </List>
        )}
      </Paper>

      {/* Modali (Modifica Evento, Modifica Sessione) */}
      <Dialog open={isEditModalOpen} onClose={handleCloseEditModal} maxWidth="md" fullWidth>
        <DialogTitle>{t('modal_edit_event_title')}</DialogTitle>
        <Box component="form" onSubmit={handleUpdateEvent}>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5, mb: 2.5, mt: 1 }}>
              <TextField name="eventName" label={t('event_name_label')} value={editFormData.eventName} onChange={handleEditFormChange} required fullWidth sx={{ flex: 1 }} disabled={isSubmitting} />
              <Autocomplete options={tracksList} getOptionLabel={(option) => option.nome || ''} value={editFormData.selectedTrack}
                onChange={(event, newValue) => { setEditFormData(prev => ({ ...prev, selectedTrack: newValue })); }}
                isOptionEqualToValue={(option, value) => option.id === value.id} loading={isLoadingTracksList}
                renderInput={(params) => ( <TextField {...params} label={t('track_label')} fullWidth /> )}
                sx={{ flex: 1 }} disabled={isSubmitting}
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5, alignItems: 'center' }}>
              <TextField name="startDate" label={t('start_date_label')} type="date" value={editFormData.startDate} onChange={handleEditFormChange} required fullWidth InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} disabled={isSubmitting} />
              <TextField name="endDate" label={t('end_date_label')} type="date" value={editFormData.endDate} onChange={handleEditFormChange} required fullWidth InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} disabled={isSubmitting} />
            </Box>
            <Box sx={{ mt: 2.5 }}>
              <TextField
                name="liveTimingUrl"
                label={t('live_timing_url_label')}
                placeholder={t('live_timing_url_placeholder')}
                value={editFormData.liveTimingUrl}
                onChange={handleEditFormChange}
                fullWidth
                disabled={isSubmitting}
                helperText={t('live_timing_url_helper')}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button onClick={handleCloseEditModal} disabled={isSubmitting}> {t('modal_general_cancel')} </Button>
            <Button type="submit" variant="contained" disabled={isSubmitting || isLoadingTracksList || !editFormData.eventName}>
              {isSubmitting ? <CircularProgress size={24} /> : t('modal_general_save_changes')}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
      
      <Dialog open={isSessionModalOpen} onClose={handleCloseEditSessionModal} maxWidth="md" fullWidth>
        <DialogTitle>{t('modal_edit_session_title')}</DialogTitle>
        <Box component="form" onSubmit={handleUpdateSession}>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5, alignItems: 'flex-start', mt: 1 }}>
              <TextField 
                name="sessionName"
                label={t('session_name_label')}
                value={editSessionFormData.sessionName} 
                onChange={handleEditSessionFormChange} 
                required 
                fullWidth
                sx={{ flex: 3 }}
                disabled={isSubmitting}
              />
              <TextField 
                name="startTime"
                label={t('session_time_label')}
                type="datetime-local" 
                value={editSessionFormData.startTime} 
                onChange={handleEditSessionFormChange} 
                required 
                fullWidth 
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 3 }}
                disabled={isSubmitting}
              />
              <TextField 
                name="duration"
                label={t('session_duration_label')}
                type="number" 
                value={editSessionFormData.duration} 
                onChange={handleEditSessionFormChange} 
                required 
                fullWidth
                sx={{ flex: 1 }}
                disabled={isSubmitting}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button onClick={handleCloseEditSessionModal} disabled={isSubmitting}>
              {t('modal_general_cancel')}
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={isSubmitting || !editSessionFormData.sessionName}
            >
              {isSubmitting ? <CircularProgress size={24} /> : t('modal_general_save_changes')}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
      
      <AssignVehiclesModal
        open={isVehicleModalOpen}
        onClose={() => setIsVehicleModalOpen(false)}
        allVehicles={allVehicles}
        assignedVehicleIds={assignedVehicleIds}
        onSave={handleSaveVehicleAssignments}
        t={t} // Passa t
      />

      <ManageVehicleChecklistsModal
        open={isVehicleChecklistModalOpen}
        onClose={handleCloseVehicleChecklistModal}
        allTemplates={allChecklistTemplates}
        vehicle={currentVehicleForModal}
        eventChecklists={allEventChecklists} 
        onSave={handleSaveVehicleChecklists}
        teamId={teamId}
        t={t} // Passa t
      />
    </>
  );
}

export default EventDetail;